import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import { useToast } from '@/ui'
import { Btn, Cta, Footer, ItemRow, Row, Strip, V1Screen, WaitCta } from '@/features/gate/v1/kit'
import { Rich } from '@/features/gate/v1/Rich'
import { C } from '@/features/gate/v1/style'
import { shortCode, shortLot, tr } from '@/features/gate/v1/text'
import type { CountBayPayload } from '../commands'
import '../commands'
import { bagsAt, placePath, type StoreState } from '../projection'
import { classifyScan, compareCount } from '../rules'
import { useRefusalText } from '../components/useRefusalText'
import { useDraftState, useNow } from '../useStore'
import { Hint, IconBox, Saved, StoreHeader, Tally, WithStore, agoText } from './bits'
import { SIcon, type StoreIconName } from './icons'
import { PlacePicker } from './PlacePicker'
import { TagScanner } from './TagScanner'

interface CountDraft {
  count_id: string
  location_id: string | null
  started_at: string | null
  codes: string[]
}

interface Counted {
  count_id: string
  /** The count_bay command: a replacement here depends on it. */
  command_id: string
  location_id: string
  place: string
  seen: number
  expected: number
  matches: boolean
}

/**
 * V1 — खाने की गिनती (F5.6): scan the place label (or tap a count the office asked for), then every
 * tag there; the phone compares with the bags it expects at the place and below and sends one
 * count_bay on गिनती पूरी. Same rules as the full count screen (CountFlow), on the boards' look.
 */
export function V1Count() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const home = () => navigate('/m/store')
  return (
    <WithStore onBack={home}>
      {(state) => (
        <CountFlow state={state} initialLocationId={params.get('location')} onHome={home} />
      )}
    </WithStore>
  )
}

function CountFlow({
  state,
  initialLocationId,
  onHome,
}: {
  state: StoreState
  initialLocationId: string | null
  onHome: () => void
}) {
  const navigate = useNavigate()
  const actor = useActor()
  const toast = useToast()
  const now = useNow()
  const refusal = useRefusalText()
  const [draft, update, loaded, clear] = useDraftState<CountDraft>(
    `store.v1.count:${state.siteId}`,
    'store.v1.count',
    () => ({ count_id: newId(), location_id: initialLocationId, started_at: null, codes: [] }),
    actor.user_id,
  )
  const [saved, setSaved] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState<Counted | null>(null)

  const location = draft.location_id ? (state.locations.get(draft.location_id) ?? null) : null
  const expected = useMemo(() => (location ? bagsAt(state, location.id) : []), [state, location])
  const cmp = useMemo(
    () => compareCount(state, expected, draft.codes),
    [state, expected, draft.codes],
  )
  const requests = state.cache.count_requests.filter((r) => !state.counts.has(r.location_id))
  const placeCodes = useMemo(() => [...state.locationByCode.keys()], [state.locationByCode])
  const bagPayloads = useMemo(() => [...state.bagByPayload.keys()], [state.bagByPayload])

  const pickPlace = (locationId: string) =>
    update((d) => ({
      ...d,
      location_id: locationId,
      codes: [],
      started_at: new Date().toISOString(),
    }))

  const onPlace = (code: string): string | null => {
    const k = classifyScan(state, code)
    if (k.kind !== 'LOCATION') return refusal('TAG_WRONG_KIND')
    if (!k.location.active) return refusal('LOCATION_INACTIVE')
    pickPlace(k.location.id)
    return null
  }

  const onTag = (code: string): string | null => {
    if (draft.codes.includes(code)) return null
    const k = classifyScan(state, code)
    if (k.kind === 'LOCATION') {
      // The counted place's own label, still in front of the camera: nothing to say.
      if (k.location.id === location?.id) return null
      return tr('store.v1.count.placeLabel')
    }
    update((d) => (d.codes.includes(code) ? d : { ...d, codes: [...d.codes, code] }))
    setSaved(code)
    return null
  }

  const removeLast = () => {
    setSaved(null)
    update((d) => ({ ...d, codes: d.codes.slice(0, -1) }))
  }

  const finish = async () => {
    if (!location) return
    setSaving(true)
    try {
      const payload: CountBayPayload = {
        count: {
          id: draft.count_id,
          location_id: location.id,
          qr_payloads: draft.codes,
          started_at: draft.started_at ?? new Date().toISOString(),
          ended_at: new Date().toISOString(),
        },
      }
      const parents = [
        ...new Set(expected.map((b) => b.pending_command_id).filter((x): x is string => !!x)),
      ]
      const rec = await enqueueCommand({
        type: 'count_bay',
        payload,
        actorId: actor.user_id,
        parentIds: parents,
      })
      setDone({
        count_id: draft.count_id,
        command_id: rec.id,
        location_id: location.id,
        place: location.name ?? location.code,
        seen: cmp.seen.length,
        expected: cmp.expected.length,
        matches: cmp.matches,
      })
      await clear()
    } catch (e) {
      toast.error(tr('store.v1.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (done)
    return (
      <CountDone
        done={done}
        onHome={onHome}
        onReplace={() =>
          navigate(
            `/m/store/replace?location=${encodeURIComponent(done.location_id)}&count=${encodeURIComponent(done.count_id)}&cmd=${encodeURIComponent(done.command_id)}`,
          )
        }
      />
    )
  if (!loaded) return <V1Screen />

  // Step 1: which place?
  if (!location)
    return (
      <V1Screen
        header={
          <StoreHeader
            onBack={onHome}
            icon="ticks"
            title={tr('store.v1.count.bayTitle')}
            step={1}
          />
        }
        footer={
          <Footer>
            <WaitCta>{tr('store.v1.count.scanPlace')}</WaitCta>
          </Footer>
        }
      >
        <Hint icon="tag" text={tr('store.v1.count.placeFirst')} />
        <TagScanner
          hint={tr('store.v1.count.scanPlace')}
          payloads={placeCodes}
          onCode={onPlace}
          kind="place"
        />
        {requests.length > 0 && (
          <>
            <b style={{ fontSize: 17 }}>{tr('store.v1.count.asked', { count: requests.length })}</b>
            {requests.map((r) => {
              const loc = state.locations.get(r.location_id)
              return (
                <ItemRow
                  key={r.id}
                  onClick={() => pickPlace(r.location_id)}
                  border={C.border}
                  padding="8px 10px"
                  lead={<IconBox icon="godown" size={44} iconSize={26} />}
                  title={loc ? (loc.name ?? loc.code) : '—'}
                  line={
                    <span style={{ fontSize: 15, color: C.muted }}>
                      {[r.note, agoText(r.requested_at, now)].filter(Boolean).join(' · ')}
                    </span>
                  }
                  right={
                    <span style={{ color: C.acc, display: 'flex' }}>
                      <SIcon name="next" size={26} sw={2.5} />
                    </span>
                  }
                />
              )
            })}
          </>
        )}
        <PlacePicker state={state} onPick={pickPlace} />
      </V1Screen>
    )

  // Step 2: every tag at the place.
  const off = cmp.extra.length + cmp.unknown.length
  const scanned = draft.codes.length > 0
  return (
    <V1Screen
      header={
        <StoreHeader onBack={onHome} icon="ticks" title={tr('store.v1.count.bayTitle')} step={2} />
      }
      footer={
        <Footer>
          {!scanned && (
            <Row>
              <Btn
                icon="back"
                onClick={() =>
                  update((d) => ({ ...d, location_id: null, codes: [], started_at: null }))
                }
              >
                {tr('store.v1.put.change')}
              </Btn>
            </Row>
          )}
          <Cta
            label={tr('store.v1.count.done')}
            icon="ok"
            busy={saving}
            onClick={() => void finish()}
          />
        </Footer>
      }
    >
      {saved && <Saved text={tr('store.v1.tag', { tag: shortCode(saved) })} />}
      <ItemRow
        lead={<IconBox icon="godown" size={44} iconSize={26} />}
        title={location.name ?? location.code}
        line={placePath(state, location.id)}
        right={
          <Tally
            value={`${cmp.seen.length}/${cmp.expected.length}`}
            label={tr('store.v1.count.counted')}
          />
        }
      />
      {!scanned && <Hint icon="scan" text={tr('store.v1.count.everyTag')} />}
      {off > 0 && (
        <Hint icon="bang" tone="warn" text={tr('store.v1.count.notExpected', { count: off })} />
      )}
      <TagScanner
        hint={tr('store.v1.scanOn.hold')}
        payloads={bagPayloads}
        onCode={onTag}
        side={
          scanned
            ? { icon: 'undo', label: tr('store.v1.put.removeLast'), onClick: removeLast }
            : undefined
        }
      />
      {cmp.byLot.length === 0 && <Strip icon="sack">{tr('store.v1.count.nothing')}</Strip>}
      {cmp.byLot.map((l) => {
        const lot = state.lots.get(l.unit_id)
        const red = lot?.stream === 'CONV'
        return (
          <ItemRow
            key={l.unit_id}
            padding="6px 10px"
            lead={
              <span style={{ color: red ? C.ink : C.ok, display: 'flex' }}>
                <SIcon name={red ? 'redtie' : 'sack'} size={26} />
              </span>
            }
            title={tr('store.v1.count.band', {
              grade: tr(red ? 'store.v1.band.red' : 'store.v1.band.good'),
              lot: lot ? shortLot(lot.code) : '—',
              n: l.seen,
              m: l.expected,
            })}
            right={
              l.short === 0 ? (
                <SIcon name="ok" size={22} sw={3} />
              ) : scanned ? (
                <b style={{ fontSize: 16, color: C.accDark, whiteSpace: 'nowrap' }}>
                  {tr('store.v1.count.short', { n: l.short })}
                </b>
              ) : undefined
            }
          />
        )
      })}
      {cmp.extra.length > 0 && (
        <Hint
          icon="bang"
          tone="warn"
          size={16}
          text={tr('store.v1.count.extra', {
            tags: cmp.extra
              .map(
                (b) =>
                  `${shortCode(b.qr_payload)} (${shortLot(state.lots.get(b.unit_id)?.code ?? '—')})`,
              )
              .join(', '),
          })}
        />
      )}
      {cmp.unknown.length > 0 && (
        <Hint
          icon="bang"
          tone="warn"
          size={16}
          text={tr('store.v1.count.unknown', { tags: cmp.unknown.map(shortCode).join(', ') })}
        />
      )}
    </V1Screen>
  )
}

/**
 * The green done screen (as DoneScreen in ./bits) with one more button: फटी बोरी / टैग गायब goes on
 * to the torn-bag screen with this count (F5-B1.a), as the full count screen does.
 */
function CountDone({
  done,
  onHome,
  onReplace,
}: {
  done: Counted
  onHome: () => void
  onReplace: () => void
}) {
  const chips: Array<{ icon: StoreIconName; text: string }> = [
    {
      icon: done.matches ? 'ticks' : 'bang',
      text: tr(done.matches ? 'store.v1.count.chipMatch' : 'store.v1.count.chipMismatch'),
    },
  ]
  return (
    <V1Screen
      main={{ padding: '0', gap: 0 }}
      footer={
        <Footer>
          <Row>
            <Btn icon="bang" onClick={onReplace}>
              {tr('store.v1.count.replace')}
            </Btn>
          </Row>
          <Cta label={tr('store.v1.home')} icon="home" lead onClick={onHome} />
        </Footer>
      }
    >
      <div
        role="status"
        style={{
          flexGrow: 1,
          background: C.ok,
          color: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 20,
          textAlign: 'center',
        }}
      >
        <span
          style={{
            width: 120,
            height: 120,
            borderRadius: 60,
            background: '#ffffff',
            color: C.ok,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SIcon name="ok" size={76} sw={3.2} />
        </span>
        <h1 style={{ margin: 0, fontSize: 30, lineHeight: 1.2 }}>
          {tr(done.matches ? 'store.v1.count.doneMatch' : 'store.v1.count.doneSaved')}
        </h1>
        <span style={{ fontSize: 20, lineHeight: 1.4 }}>
          <Rich
            text={tr('store.v1.count.doneLine', {
              seen: done.seen,
              expected: done.expected,
              place: done.place,
            })}
          />
        </span>
        {chips.map((c) => (
          <span
            key={c.text}
            style={{
              fontSize: 18,
              lineHeight: 1.4,
              padding: '8px 12px',
              borderRadius: 12,
              background: 'rgba(0,0,0,.18)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <SIcon name={c.icon} size={22} />
            <span>{c.text}</span>
          </span>
        ))}
      </div>
    </V1Screen>
  )
}
